import {
  Button,
  Card,
  CardHeader,
  FormField,
  InlineAlert,
  SkeletonRows,
  Spinner,
  TextInput,
} from "@constructorfleet/ui/design-system";
import { RefreshIcon, ResponsiveCardGrid } from "@constructorfleet/ui";
import { useCallback, useEffect, useState } from "react";
import { useConfirmAction } from "./confirm-action";
import { StatusBadge, DiscrepancyBadge } from "./status-badges";
import type { UaProductDbClient } from "../pages";
import type { ActiveCallEntry } from "../ua-call-settings";
import {
  loadUaActiveCalls,
  requestUaManualHangup,
  resolveUaStaleCallFromAdmin,
} from "../ua-call-settings";

export function ActiveCallsPanel({ dbClient }: { dbClient: UaProductDbClient }) {
  const [entries, setEntries] = useState<ActiveCallEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [providerWarning, setProviderWarning] = useState<string | null>(null);
  const [manualCallSid, setManualCallSid] = useState("");
  const [hangupSubmitting, setHangupSubmitting] = useState(false);
  const [resolving, setResolving] = useState<Set<number>>(new Set());

  const requestAdmin = dbClient.requestAdmin;

  const refresh = useCallback(async () => {
    if (!requestAdmin) return;
    setLoading(true);
    setMessage(null);
    setProviderWarning(null);
    const result = await loadUaActiveCalls(requestAdmin);
    setLoading(false);
    if (!result.ok) {
      setMessage(result.error);
      return;
    }
    setEntries(result.entries);
    if (result.providerError) {
      setProviderWarning(`Provider unavailable: ${result.providerError}`);
    }
    if (result.entries.length === 0 && !result.providerError) {
      setMessage("No active calls found.");
    }
  }, [requestAdmin]);

  useEffect(() => {
    void refresh();
    const interval = setInterval(() => void refresh(), 15_000);
    return () => clearInterval(interval);
  }, [refresh]);

  const handleHangup = async (callSidInput: string) => {
    if (!requestAdmin) return;
    const callSid = callSidInput.trim();
    if (!callSid) {
      setMessage("Call ID is required.");
      return;
    }
    setHangupSubmitting(true);
    try {
      const result = await requestUaManualHangup(callSid, requestAdmin);
      if (!result.ok) {
        setMessage(result.error);
        return;
      }
      setMessage(`Hangup requested for ${callSid}.`);
      setManualCallSid("");
      void refresh();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Failed to hang up call.");
    } finally {
      setHangupSubmitting(false);
    }
  };

  const handleResolve = async (callId: number, resolution: "completed" | "failed") => {
    if (!requestAdmin) return;
    setResolving((prev) => new Set([...prev, callId]));
    try {
      const result = await resolveUaStaleCallFromAdmin(callId, resolution, requestAdmin);
      if (!result.ok) {
        setMessage(result.error);
        return;
      }
      setMessage(`Call ${callId} resolved as ${resolution}.`);
      void refresh();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Failed to resolve call.");
    } finally {
      setResolving((prev) => {
        const next = new Set(prev);
        next.delete(callId);
        return next;
      });
    }
  };

  const { requestConfirm, confirmModal } = useConfirmAction();

  const manualCallSidTrimmed = manualCallSid.trim();

  const hasDiscrepancies = entries.some((e) => {
    if (!e.dbCall) return false;
    const providerTerminal = !e.providerActive;
    const dbTerminal = e.dbCall.status === "completed" || e.dbCall.status === "failed";
    return providerTerminal !== dbTerminal;
  });

  return (
    <Card>
      <CardHeader
        title="Active Calls"
        meta={`${entries.length} active call${entries.length === 1 ? "" : "s"} visible. Polling every 15s.`}
        action={
          <div className="shell-actions">
            {hasDiscrepancies ? (
              <InlineAlert tone="danger">Discrepancies detected</InlineAlert>
            ) : null}
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => void refresh()}
              disabled={loading}
              leadingIcon={<RefreshIcon />}
            >
              Refresh
            </Button>
          </div>
        }
      />
      <div className="card-body ds-stack">
        {confirmModal}
        <div
          className="card"
          style={{
            display: "grid",
            gap: "var(--cf-space-sm)",
            marginBottom: "var(--cf-space-sm)",
          }}
        >
          <div>
            <strong>Manual Hangup</strong>
            <p style={{ color: "var(--cf-text-muted)", fontSize: "13px", margin: "0.35rem 0 0" }}>
              Enter a call control ID and force a hangup even when the live active-call lookup is
              degraded.
            </p>
          </div>
          <FormField
            label="Call control ID"
            htmlFor="ua-manual-hangup-call-sid"
            helpText="Use the Telnyx/Twilio call control SID shown in call history or runtime logs."
          >
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <TextInput
                id="ua-manual-hangup-call-sid"
                value={manualCallSid}
                onChange={(event) =>
                  setManualCallSid((event.target as HTMLInputElement).value ?? "")
                }
                placeholder="ua_manual_..."
                autoComplete="off"
                spellCheck={false}
              />
              <Button
                type="button"
                className="btn-danger"
                disabled={manualCallSidTrimmed.length === 0 || hangupSubmitting}
                onClick={() => {
                  requestConfirm({
                    title: "Hang up call",
                    message: `Hang up call ${manualCallSidTrimmed}? This will immediately terminate the active call.`,
                    confirmLabel: "Hang up",
                    tone: "danger",
                    onConfirm: () => void handleHangup(manualCallSidTrimmed),
                  });
                }}
              >
                {hangupSubmitting ? "..." : "Hang up by ID"}
              </Button>
            </div>
          </FormField>
        </div>
        {providerWarning ? (
          <InlineAlert tone="neutral">
            {providerWarning} -- Live provider activity may be stale. Manual hangup remains
            available.
          </InlineAlert>
        ) : null}
        {message ? (
          <InlineAlert
            tone={message.includes("error") || message.includes("fail") ? "danger" : "neutral"}
          >
            {message}
          </InlineAlert>
        ) : null}
        <div style={{ position: "relative" }}>
          {loading && entries.length > 0 ? (
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "var(--cf-bg-subtle)",
                zIndex: 1,
                borderRadius: "6px",
              }}
            >
              <Spinner size="md" label="Refreshing..." />
            </div>
          ) : null}

          {entries.length > 0 ? (
            <ResponsiveCardGrid minWidth={280} gap="0.5rem">
              {entries.map((entry) => {
                const canAttemptHangup =
                  entry.providerActive ||
                  (entry.dbCall != null &&
                    entry.dbCall.status !== "completed" &&
                    entry.dbCall.status !== "failed");
                const needsResolution =
                  entry.dbCall &&
                  !entry.providerActive &&
                  entry.dbCall.status !== "completed" &&
                  entry.dbCall.status !== "failed";
                const isResolving = entry.dbCall ? resolving.has(entry.dbCall.id) : false;

                return (
                  <div
                    className="card"
                    key={entry.callSid}
                    style={{
                      display: "grid",
                      gap: "var(--cf-space-sm)",
                      gridTemplateColumns: "1fr auto",
                      alignItems: "center",
                    }}
                  >
                    <div style={{ display: "grid", gap: "0.25rem" }}>
                      <div
                        style={{
                          display: "flex",
                          gap: "var(--cf-space-sm)",
                          alignItems: "center",
                          flexWrap: "wrap",
                        }}
                      >
                        <strong
                          style={{
                            fontFamily: "monospace",
                            fontSize: "0.85rem",
                            wordBreak: "break-all",
                          }}
                        >
                          {entry.callSid}
                        </strong>
                        {entry.dbCall ? <StatusBadge status={entry.dbCall.status} /> : null}
                        <DiscrepancyBadge
                          providerActive={entry.providerActive}
                          dbStatus={entry.dbCall?.status ?? null}
                        />
                      </div>
                      <div
                        style={{
                          fontSize: "0.8rem",
                          color: "var(--cf-muted-text)",
                          display: "flex",
                          gap: "var(--cf-space-md)",
                          flexWrap: "wrap",
                        }}
                      >
                        {entry.dbCall ? (
                          <>
                            <span>ID: {entry.dbCall.id}</span>
                            <span>User: {entry.dbCall.userId}</span>
                            <span>Started: {entry.dbCall.startedAt}</span>
                            {entry.dbCall.voiceProvider ? (
                              <span>Provider: {entry.dbCall.voiceProvider}</span>
                            ) : null}
                            {entry.dbCall.errorReason ? (
                              <span style={{ color: "var(--cf-error)" }}>
                                Error: {entry.dbCall.errorReason}
                              </span>
                            ) : null}
                          </>
                        ) : (
                          <span>Provider-only call (no database record)</span>
                        )}
                      </div>
                    </div>
                    <div className="shell-actions" style={{ marginTop: 0 }}>
                      {canAttemptHangup ? (
                        <Button
                          type="button"
                          size="sm"
                          className="btn-danger"
                          disabled={hangupSubmitting}
                          onClick={() => {
                            requestConfirm({
                              title: "Hang up call",
                              message: `Hang up call ${entry.callSid}? This will immediately terminate the active call.`,
                              confirmLabel: "Hang up",
                              tone: "danger",
                              onConfirm: () => void handleHangup(entry.callSid),
                            });
                          }}
                        >
                          {hangupSubmitting ? "..." : "Hang up"}
                        </Button>
                      ) : null}
                      {needsResolution ? (
                        <>
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            disabled={isResolving}
                            onClick={() => void handleResolve(entry.dbCall!.id, "completed")}
                          >
                            {isResolving ? "..." : "Mark completed"}
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            className="btn-danger"
                            disabled={isResolving}
                            onClick={() => void handleResolve(entry.dbCall!.id, "failed")}
                          >
                            {isResolving ? "..." : "Mark failed"}
                          </Button>
                        </>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </ResponsiveCardGrid>
          ) : loading ? (
            <div style={{ padding: "var(--cf-space-md)" }}>
              <SkeletonRows count={3} />
            </div>
          ) : null}
        </div>
      </div>
    </Card>
  );
}
